import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, LogOut, Check, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useTitle } from "@/lib/img";
import { ImageUpload } from "@/components/ImageUpload";

const CATEGORIES = ["Residential", "Hospitality", "Workplace", "Retail", "Winery & Tasting Room", "Adaptive Reuse"];

const EMPTY_PROJECT = {
  name: "", slug: "", category: "Residential", location: "", year: "", featured_medium: "",
  scope: "", description: "", hero_image: "", imagesText: "", creditsText: "", featured: false, order: 99,
};

const EMPTY_PRESS = { publication: "", title: "", year: new Date().getFullYear(), date: "", excerpt: "", link: "", image: "", order: 99 };

function projectToForm(p) {
  return {
    ...p,
    imagesText: (p.images || []).join("\n"),
    creditsText: (p.credits || []).map((c) => `${c.role}: ${c.name}`).join("\n"),
  };
}

function formToProject(f) {
  return {
    name: f.name, slug: f.slug, category: f.category, location: f.location, year: f.year,
    featured_medium: f.featured_medium, scope: f.scope, description: f.description,
    hero_image: f.hero_image,
    images: f.imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
    credits: f.creditsText.split("\n").map((line) => {
      const idx = line.indexOf(":");
      return idx > 0 ? { role: line.slice(0, idx).trim(), name: line.slice(idx + 1).trim() } : null;
    }).filter(Boolean),
    featured: !!f.featured,
    order: Number(f.order) || 99,
  };
}

const inputCls = "w-full bg-paper border border-line px-3 py-2.5 text-sm focus:outline-none focus:border-ink transition-colors rounded-none";
const labelCls = "block text-[10px] uppercase tracking-[0.2em] text-smoke mb-1.5";

export default function AdminDashboard() {
  useTitle("Dashboard | Medium Plenty Admin");
  const navigate = useNavigate();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [press, setPress] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [editingPress, setEditingPress] = useState(null);

  const load = useCallback(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
    api.get("/press").then(({ data }) => setPress(data)).catch(() => {});
    api.get("/admin/inquiries").then(({ data }) => setInquiries(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("mp_token")) {
      navigate("/admin/login");
      return;
    }
    api.get("/auth/me").then(load).catch(() => {
      localStorage.removeItem("mp_token");
      navigate("/admin/login");
    });
  }, [navigate, load]);

  const onAuthError = (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("mp_token");
      navigate("/admin/login");
    } else {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveProject = async () => {
    try {
      const payload = formToProject(editingProject);
      if (editingProject.id) await api.put(`/admin/projects/${editingProject.id}`, payload);
      else await api.post("/admin/projects", payload);
      toast.success("Project saved");
      setEditingProject(null);
      load();
    } catch (err) {
      onAuthError(err);
    }
  };

  const deleteProject = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/projects/${p.id}`);
      toast.success("Project deleted");
      load();
    } catch (err) {
      onAuthError(err);
    }
  };

  const savePress = async () => {
    try {
      const { id, ...rest } = editingPress;
      const payload = { ...rest, year: Number(rest.year) || 2024, order: Number(rest.order) || 99 };
      if (id) await api.put(`/admin/press/${id}`, payload);
      else await api.post("/admin/press", payload);
      toast.success("Press item saved");
      setEditingPress(null);
      load();
    } catch (err) {
      onAuthError(err);
    }
  };

  const deletePress = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await api.delete(`/admin/press/${item.id}`);
      toast.success("Press item deleted");
      load();
    } catch (err) {
      onAuthError(err);
    }
  };

  const markRead = async (inq) => {
    try {
      await api.patch(`/admin/inquiries/${inq.id}/read`);
      load();
    } catch (err) {
      onAuthError(err);
    }
  };

  const deleteInquiry = async (inq) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/admin/inquiries/${inq.id}`);
      load();
    } catch (err) {
      onAuthError(err);
    }
  };

  const logout = () => {
    localStorage.removeItem("mp_token");
    navigate("/admin/login");
  };

  const setP = (k) => (e) => setEditingProject((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const setPr = (k) => (e) => setEditingPress((f) => ({ ...f, [k]: e.target.value }));

  const unread = inquiries.filter((i) => !i.read).length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-paper text-ink">
      <header className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-line">
        <div>
          <p className="font-serif text-xl tracking-tight">Medium Plenty</p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-smoke">Studio Admin</p>
        </div>
        <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 text-sm text-smoke hover:text-ink transition-colors">
          <LogOut size={15} /> Sign out
        </button>
      </header>

      <div className="px-6 md:px-10 py-6 flex gap-8 border-b border-line">
        {[
          ["projects", "Projects"],
          ["press", "Press"],
          ["inquiries", `Inquiries${unread ? ` (${unread})` : ""}`],
        ].map(([key, label]) => (
          <button
            key={key}
            data-testid={`admin-tab-${key}`}
            onClick={() => setTab(key)}
            data-active={tab === key}
            className={`editorial-link text-sm tracking-[0.08em] uppercase ${tab === key ? "text-ink" : "text-smoke/60 hover:text-ink"}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="px-6 md:px-10 py-10">
        {tab === "projects" && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <p className="text-sm text-smoke">{projects.length} projects</p>
              <button
                data-testid="admin-new-project-btn"
                onClick={() => setEditingProject({ ...EMPTY_PROJECT })}
                className="inline-flex items-center gap-2 border border-ink px-5 py-2.5 text-xs tracking-[0.1em] uppercase hover:bg-ink hover:text-paper transition-colors"
              >
                <Plus size={14} /> New Project
              </button>
            </div>
            <div className="space-y-px">
              {projects.map((p) => (
                <div key={p.id} data-testid={`admin-project-row-${p.slug}`} className="flex items-center gap-4 border-b border-line py-4">
                  <img src={p.hero_image} alt="" className="w-16 h-12 object-cover bg-sand" loading="lazy" />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg leading-tight truncate">{p.name}</p>
                    <p className="text-xs text-smoke">{p.category} — {p.location}</p>
                  </div>
                  <button data-testid={`admin-edit-project-${p.slug}`} onClick={() => setEditingProject(projectToForm(p))} className="p-2 text-smoke hover:text-ink" aria-label={`Edit ${p.name}`}>
                    <Pencil size={16} />
                  </button>
                  <button data-testid={`admin-delete-project-${p.slug}`} onClick={() => deleteProject(p)} className="p-2 text-smoke hover:text-red-800" aria-label={`Delete ${p.name}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>

            {editingProject && (
              <div data-testid="admin-project-editor" className="fixed inset-0 z-50 bg-ink/40 flex justify-end" onClick={() => setEditingProject(null)}>
                <div className="w-full max-w-xl h-full bg-paper overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-8">
                    <p className="font-serif text-2xl">{editingProject.id ? "Edit Project" : "New Project"}</p>
                    <button data-testid="admin-project-editor-close" onClick={() => setEditingProject(null)} aria-label="Close editor"><X size={20} /></button>
                  </div>
                  <div className="space-y-5">
                    <div><label className={labelCls}>Name *</label><input data-testid="admin-project-name-input" className={inputCls} value={editingProject.name} onChange={setP("name")} /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><label className={labelCls}>Slug</label><input className={inputCls} value={editingProject.slug} onChange={setP("slug")} placeholder="auto from name" /></div>
                      <div>
                        <label className={labelCls}>Category</label>
                        <select data-testid="admin-project-category-select" className={inputCls} value={editingProject.category} onChange={setP("category")}>
                          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div><label className={labelCls}>Location</label><input className={inputCls} value={editingProject.location} onChange={setP("location")} /></div>
                      <div><label className={labelCls}>Year</label><input className={inputCls} value={editingProject.year} onChange={setP("year")} /></div>
                      <div><label className={labelCls}>Featured Medium</label><input className={inputCls} value={editingProject.featured_medium} onChange={setP("featured_medium")} /></div>
                      <div><label className={labelCls}>Order</label><input className={inputCls} type="number" value={editingProject.order} onChange={setP("order")} /></div>
                    </div>
                    <div><label className={labelCls}>Scope</label><input className={inputCls} value={editingProject.scope} onChange={setP("scope")} placeholder="Architecture & Interior Design" /></div>
                    <div><label className={labelCls}>Description</label><textarea data-testid="admin-project-description-input" rows={5} className={inputCls} value={editingProject.description} onChange={setP("description")} /></div>
                    <div>
                      <label className={labelCls}>Hero Image</label>
                      <input data-testid="admin-project-hero-input" className={inputCls} value={editingProject.hero_image} onChange={setP("hero_image")} placeholder="https://… or upload" />
                      <div className="mt-2">
                        <ImageUpload testid="admin-hero-upload-btn" onUploaded={(url) => setEditingProject((f) => ({ ...f, hero_image: url }))} />
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Gallery Images — one URL per line</label>
                      <textarea data-testid="admin-project-gallery-input" rows={6} className={inputCls} value={editingProject.imagesText} onChange={setP("imagesText")} />
                      <div className="mt-2">
                        <ImageUpload
                          multiple
                          testid="admin-gallery-upload-btn"
                          label="Upload images"
                          onUploaded={(url) => setEditingProject((f) => ({ ...f, imagesText: f.imagesText ? `${f.imagesText}\n${url}` : url }))}
                        />
                      </div>
                    </div>
                    <div><label className={labelCls}>Credits — "Role: Name" per line</label><textarea rows={4} className={inputCls} value={editingProject.creditsText} onChange={setP("creditsText")} /></div>
                    <label className="flex items-center gap-3 text-sm">
                      <input data-testid="admin-project-featured-checkbox" type="checkbox" checked={editingProject.featured} onChange={setP("featured")} className="accent-[#A88B7D]" />
                      Feature on homepage
                    </label>
                    <button data-testid="admin-save-project-btn" onClick={saveProject} disabled={!editingProject.name} className="w-full bg-ink text-paper py-3.5 text-xs tracking-[0.15em] uppercase hover:opacity-90 transition-opacity disabled:opacity-40">
                      Save Project
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "press" && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <p className="text-sm text-smoke">{press.length} press items</p>
              <button
                data-testid="admin-new-press-btn"
                onClick={() => setEditingPress({ ...EMPTY_PRESS })}
                className="inline-flex items-center gap-2 border border-ink px-5 py-2.5 text-xs tracking-[0.1em] uppercase hover:bg-ink hover:text-paper transition-colors"
              >
                <Plus size={14} /> New Press Item
              </button>
            </div>
            {press.map((item) => (
              <div key={item.id} className="flex items-center gap-4 border-b border-line py-4">
                {item.image && <img src={item.image} alt="" className="w-16 h-12 object-cover bg-sand" loading="lazy" />}
                <span className="text-xs text-smoke w-14 shrink-0">{item.year}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-lg leading-tight truncate">{item.title}</p>
                  <p className="text-xs text-smoke">{item.publication}</p>
                </div>
                <button onClick={() => setEditingPress({ ...item })} className="p-2 text-smoke hover:text-ink" aria-label={`Edit ${item.title}`} data-testid={`admin-edit-press-${item.id}`}>
                  <Pencil size={16} />
                </button>
                <button onClick={() => deletePress(item)} className="p-2 text-smoke hover:text-red-800" aria-label={`Delete ${item.title}`} data-testid={`admin-delete-press-${item.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}

            {editingPress && (
              <div data-testid="admin-press-editor" className="fixed inset-0 z-50 bg-ink/40 flex justify-end" onClick={() => setEditingPress(null)}>
                <div className="w-full max-w-xl h-full bg-paper overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-8">
                    <p className="font-serif text-2xl">{editingPress.id ? "Edit Press Item" : "New Press Item"}</p>
                    <button onClick={() => setEditingPress(null)} aria-label="Close editor" data-testid="admin-press-editor-close"><X size={20} /></button>
                  </div>
                  <div className="space-y-5">
                    <div><label className={labelCls}>Publication *</label><input data-testid="admin-press-publication-input" className={inputCls} value={editingPress.publication} onChange={setPr("publication")} /></div>
                    <div><label className={labelCls}>Title *</label><input data-testid="admin-press-title-input" className={inputCls} value={editingPress.title} onChange={setPr("title")} /></div>
                    <div className="grid grid-cols-3 gap-4">
                      <div><label className={labelCls}>Year</label><input className={inputCls} type="number" value={editingPress.year} onChange={setPr("year")} /></div>
                      <div><label className={labelCls}>Date Label</label><input className={inputCls} value={editingPress.date} onChange={setPr("date")} placeholder="Fall 2025" /></div>
                      <div><label className={labelCls}>Order</label><input className={inputCls} type="number" value={editingPress.order} onChange={setPr("order")} /></div>
                    </div>
                    <div><label className={labelCls}>Excerpt</label><textarea rows={3} className={inputCls} value={editingPress.excerpt} onChange={setPr("excerpt")} /></div>
                    <div><label className={labelCls}>Article URL</label><input className={inputCls} value={editingPress.link} onChange={setPr("link")} placeholder="https://…" /></div>
                    <div>
                      <label className={labelCls}>Glimpse Image</label>
                      <input data-testid="admin-press-image-input" className={inputCls} value={editingPress.image || ""} onChange={setPr("image")} placeholder="https://… or upload" />
                      <div className="mt-2">
                        <ImageUpload testid="admin-press-upload-btn" onUploaded={(url) => setEditingPress((f) => ({ ...f, image: url }))} />
                      </div>
                    </div>
                    <button data-testid="admin-save-press-btn" onClick={savePress} disabled={!editingPress.publication || !editingPress.title} className="w-full bg-ink text-paper py-3.5 text-xs tracking-[0.15em] uppercase hover:opacity-90 transition-opacity disabled:opacity-40">
                      Save Press Item
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "inquiries" && (
          <div className="space-y-6 max-w-3xl">
            {inquiries.length === 0 && <p className="text-smoke text-sm">No inquiries yet.</p>}
            {inquiries.map((inq) => (
              <div
                key={inq.id}
                data-testid={`admin-inquiry-${inq.id}`}
                className={`border p-6 ${inq.read ? "border-line" : "border-clay bg-sand/40"}`}
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <p className="font-serif text-xl leading-tight">{inq.name}</p>
                    <p className="text-xs text-smoke mt-1">
                      {inq.email}{inq.phone ? ` · ${inq.phone}` : ""}
                    </p>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-smoke whitespace-nowrap">
                    {new Date(inq.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-xs text-smoke mb-3">
                  {[inq.project_type, inq.location, inq.budget, inq.nda ? `NDA: ${inq.nda}` : ""].filter(Boolean).join(" · ")}
                </p>
                {inq.message && <p className="text-sm leading-relaxed mb-4">{inq.message}</p>}
                <div className="flex gap-4">
                  {!inq.read && (
                    <button data-testid={`admin-inquiry-read-${inq.id}`} onClick={() => markRead(inq)} className="inline-flex items-center gap-1.5 text-xs text-smoke hover:text-ink">
                      <Check size={13} /> Mark read
                    </button>
                  )}
                  <button data-testid={`admin-inquiry-delete-${inq.id}`} onClick={() => deleteInquiry(inq)} className="inline-flex items-center gap-1.5 text-xs text-smoke hover:text-red-800">
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
